import type { HomePage } from '@/sanity/landing-page';
import { getLandingPageFaqs } from '@/utils/landing-page-faqs';

const checkedFeeSentence =
  'Full management at the 33 agents we checked in October 2026 costs 9.6% to 20.4% of the rent, including VAT.';

export const homeSearchLinks = [
  {
    href: '/blog/how-much-do-letting-agents-charge',
    label: 'How much do letting agents charge?',
  },
  {
    href: '/letting-agent-fees',
    label: 'Letting agent fees',
  },
  {
    href: '/compare-letting-agents',
    label: 'Compare letting agents',
  },
] as const;

const homeFaqSection = {
  _key: 'home-search-faq',
  _type: 'landingFaqSection' as const,
  anchorId: 'faq',
  eyebrow: 'Questions',
  title: 'Frequently asked questions',
  description: null,
  footnote: null,
  faqs: [
    {
      _key: 'how-much',
      question: 'How much do letting agents charge?',
      answer:
        'We checked 33 letting agents in London, Birmingham, Manchester, Leeds and Sheffield in October 2026. Full management costs 9.6% to 20.4% of the rent once VAT is added. Most sit between 12% and 15.6%. The fee guide breaks that down by city, in pounds.',
    },
    {
      _key: 'what-budgy-compares',
      question: 'What does Budgy compare?',
      answer:
        'Budgy compares local letting agents on management fees, reviews and client money protection, then emails a side-by-side report. Nothing goes to an agent without your sign-off.',
    },
    {
      _key: 'how-to-compare',
      question: 'How do I compare letting agents near my property?',
      answer:
        'Enter the property address. Budgy checks letting agents nearby and emails a report, usually within 10 minutes. It is free.',
    },
    {
      _key: 'where-fees',
      question: 'Where can I see published letting agent fees?',
      answer:
        'The letting agent fees page summarises the range. City guides cover London, Birmingham, Manchester, Leeds and Sheffield, using fees those agents publish, with VAT included.',
    },
  ],
};

type HomeSearchAnswersPage = {
  hero: {
    description: string;
  };
  sections: HomePage['sections'];
};

export const withHomeSearchAnswers = <Page extends HomeSearchAnswersPage>(
  homePage: Page,
): Page => {
  const description = homePage.hero.description.includes('9.6%')
    ? homePage.hero.description
    : `${homePage.hero.description} ${checkedFeeSentence}`;
  const hasFaq = getLandingPageFaqs(homePage).length > 0;
  const sections = hasFaq
    ? homePage.sections
    : [...homePage.sections, homeFaqSection];

  return {
    ...homePage,
    hero: {
      ...homePage.hero,
      description,
    },
    sections,
  };
};
